import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { createTheme } from '@mui/material/styles';
import { CustomThemeProvider } from './context/ThemeContext';
import ThemeConfig from './pages/ThemeConfig';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import Layout from './components/Layout';
import Home from './pages/Home';
import Events from './pages/Events';
import Gallery from './pages/Gallery';
import WhoWeAre from './pages/WhoWeAre';
import { LocationsPage } from './pages/LocationsPage';
import { LocationDetailsPage } from './pages/LocationDetailsPage';
import { UserManagement } from './pages/UserManagement';
import RequireAuth from './components/RequireAuth';
import RequireAdmin from './components/RequireAdmin';
import { AdminUsers } from './pages/AdminUsers';
import { Profile } from './pages/Profile';
import { Subscriptions } from './pages/Subscriptions';
import Inventory from './pages/admin/Inventory';
import { Finances } from './pages/admin/Finances';
import { Analytics } from './pages/admin/Analytics';
import { Communication } from './pages/Communication';
import { SocialGroups } from './pages/admin/SocialGroups';
import PhotoImport from './pages/PhotoImport';
import NewsEditor from './pages/NewsEditor';
import Deals from './pages/Deals';
import News from './pages/News';
import { useAnalytics } from './hooks/useAnalytics';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4a90e2', // Bleu principal
      light: '#64a6ff',
      dark: '#2176d2',
    },
    secondary: {
      main: '#1e88e5', // Bleu secondaire
      light: '#4b9fea',
      dark: '#115293',
    },
    background: {
      default: '#0a192f', // Bleu très foncé
      paper: '#172a45',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.85)',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '0.02em',
      fontSize: {
        xs: '1.75rem',
        sm: '2.5rem',
        md: '4rem',
      } as any,
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '0.01em',
      fontSize: {
        xs: '1.5rem',
        sm: '2rem',
        md: '3rem',
      } as any,
    },
    h3: {
      fontWeight: 600,
      fontSize: {
        xs: '1.25rem',
        sm: '1.5rem',
        md: '2rem',
      } as any,
    },
    h4: {
      fontWeight: 600,
      fontSize: {
        xs: '1.1rem',
        sm: '1.25rem',
        md: '1.75rem',
      } as any,
    },
    body1: {
      fontSize: {
        xs: '0.813rem',
        sm: '0.875rem',
        md: '1rem',
      } as any,
    },
    body2: {
      fontSize: {
        xs: '0.75rem',
        sm: '0.813rem',
        md: '0.9375rem',
      } as any,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.05em',
      fontSize: {
        xs: '0.813rem',
        sm: '0.875rem',
        md: '1rem',
      } as any,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: 'url("/images/RezoneArt/Rezone_Art1.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&:before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(10, 25, 47, 0.95) 0%, rgba(30, 136, 229, 0.8) 100%)',
            zIndex: -1,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(23, 42, 69, 0.95)',
          borderRadius: '8px',
          overflow: 'hidden',
          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
          border: '1px solid rgba(74, 144, 226, 0.1)',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(30, 136, 229, 0.2)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          padding: '10px 30px',
          fontSize: '0.9rem',
          fontWeight: 600,
          background: 'linear-gradient(45deg, #4a90e2 30%, #64a6ff 90%)',
          border: 'none',
          color: '#ffffff',
          boxShadow: '0 3px 15px rgba(74, 144, 226, 0.3)',
          '&:hover': {
            background: 'linear-gradient(45deg, #2176d2 30%, #4a90e2 90%)',
            boxShadow: '0 5px 20px rgba(74, 144, 226, 0.4)',
          },
        },
        outlined: {
          background: 'transparent',
          border: '2px solid #4a90e2',
          '&:hover': {
            background: 'rgba(74, 144, 226, 0.1)',
            border: '2px solid #64a6ff',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 25, 47, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(74, 144, 226, 0.2)',
        },
      },
    },
  },
});

function AppContent() {
  const { isAdmin } = useAuth();
  useAnalytics();

  return (
    <>
      <Navbar />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/events"
            element={
              <RequireAuth requireAuth={false}>
                <Events />
              </RequireAuth>
            }
          />
          <Route
            path="/user-management"
            element={
              <RequireAuth>
                <UserManagement />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/deals" element={<Deals />} />
          <Route path="/news" element={<News />} />
          <Route path="/qui-sommes-nous" element={<WhoWeAre />} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/locations/:id" element={<LocationDetailsPage />} />
          <Route path="/communication" element={
            <RequireAuth>
              <Communication />
            </RequireAuth>
          } />
          {isAdmin && (
            <>
              <Route
                path="/admin/users"
                element={
                  <RequireAuth>
                    <AdminUsers />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <RequireAuth>
                    <Subscriptions />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/inventory"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Inventory />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/finances"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Finances />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Analytics />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/photo-import"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <PhotoImport />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/social"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <SocialGroups />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/news/create"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <NewsEditor />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/news/edit/:id"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <NewsEditor />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/theme"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <ThemeConfig />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
            </>
          )}
        </Routes>
      </Layout>
    </>
  );
}


function App() {
  return (
    <AuthProvider>
      <CustomThemeProvider>
        <CssBaseline />
        <AppContent />
      </CustomThemeProvider>
    </AuthProvider>
  );
}

export default App;
